import React, { useEffect, useState, useRef } from "react";
import { DatePicker, Skeleton } from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import ScreenHeader from "../../../CommonComponents/ScreenHeader";
import DescriptionText from "../../../CommonComponents/DescriptionText";
import ImageGallery from "../../../CommonComponents/ImageGallery";
import { useInfiniteQuery } from "@tanstack/react-query";
import useChildrenGroups from "../hooks/useChildrenGroups";
import ClubGroupFilter from "../../../CommonComponents/ClubGroupFilter";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import { fetchParentGalleryImages } from "../services/gallery";
import { deleteBothImageCopies } from "../../../services/firebaseService/endPoints/clubAdmin/gallery";
import logger from "../../../utils/logger";
import { toast } from "react-toastify";
import "./ParentAdminGallery.css";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;

const ParentAdminGallery = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const observerTarget = useRef(null);

  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { childrenGroupIds, childGroupDetails } = useChildrenGroups();

  // Initialize with all groups
  useEffect(() => {
    if (childrenGroupIds && childrenGroupIds.length > 0) {
      setSelectedGroupIds(childrenGroupIds);
    }
  }, [childrenGroupIds]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "parentGalleryImages",
      groupData?.id,
      selectedGroupIds,
      dateRange?.[0] ? dateRange[0].unix() : null,
      dateRange?.[1] ? dateRange[1].unix() : null,
    ],
    queryFn: ({ pageParam }) =>
      fetchParentGalleryImages({
        parentId: groupData?.id,
        selectedGroupIds,
        dateRange,
        pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return {
          lastDocId: lastPage.lastDocId,
          lastGroupId: lastPage.lastGroupId,
          lastUploadDate: lastPage.lastUploadDate,
        };
      }
      return undefined;
    },
    enabled: !!groupData?.id && selectedGroupIds.length > 0,
  });

  useEffect(() => {
    const currentTarget = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten all pages into a single array of images
  const allImages = data?.pages?.flatMap((page) => page.images) || [];

  const extractPathFromUrl = (url) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const startIndex = decodedUrl.indexOf("/o/") + 3;
      const endIndex = decodedUrl.indexOf("?");

      return decodedUrl.substring(startIndex, endIndex);
    } catch (error) {
      console.error("Error extracting path:", error);
      return null;
    }
  };

  const deleteSelectedImage = async (imageId, urlLarge, urlSmall) => {
    try {
      setDeletingImageId(imageId);
      setDeleteLoading({ [imageId]: true });

      let pathLarge = "";
      let pathSmall = "";

      if (urlLarge && urlLarge?.length > 0) {
        pathLarge = extractPathFromUrl(urlLarge);
      }

      if (urlSmall && urlSmall?.length > 0) {
        pathSmall = extractPathFromUrl(urlSmall);
      }

      const imageData = allImages.find((img) => img.id === imageId);
      const imageGroupId = imageData?.groupId || groupData?.id;

      const success = await deleteBothImageCopies(
        pathLarge,
        pathSmall,
        imageGroupId,
        imageId
      );

      if (success) {
        toast.success("Image deleted successfully");
        setDeletingImageId(null);
        await refetch();
      } else {
        setDeletingImageId(null);
        toast.error("Failed to delete image");
        logger.error("Failed to delete image");
      }
    } catch (error) {
      toast.error("Failed to delete image");
      setDeletingImageId(null);
      logger.error("Error deleting image:", error);
    } finally {
      setDeleteLoading({ [imageId]: false });
    }
  };

  const renderImages = () => {
    const processedImages = allImages.map((img) => {
      let dimensions = img.dimensions || {
        height: 320,
        width: 320,
      };
      let { width, height } = dimensions;
      let imageHeight = 0,
        imageWidth = 0;
      if (height > width) {
        imageHeight = 480;
        imageWidth = (width / height) * imageHeight;
      } else {
        imageWidth = 480;
        imageHeight = (height / width) * imageWidth;
      }

      return {
        id: img.id,
        src: img.imageUrl,
        srcLg: img.imageUrlLg || img.imageUrl,
        width: imageWidth,
        height: imageHeight,
        uploadDate: img.uploadDate,
        tags: [
          {
            value: dayjs.unix(img.uploadDate).format("Do MMM, YYYY"),
            title: dayjs.unix(img.uploadDate).format("Do MMM, YYYY"),
          },
        ],
        onDelete: () =>
          deleteSelectedImage(img.id, img.imageUrlLg, img.imageUrl),
      };
    });

    if (processedImages.length === 0 && !isLoading) {
      return (
        <div
          style={{
            height: "500px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            marginTop: "2px",
            backgroundColor: "white",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              color: "#999",
            }}
          >
            No images found
          </div>
        </div>
      );
    }

    return (
      <>
        <ImageGallery
          loading={isLoading}
          images={processedImages}
          deletingImageId={deletingImageId}
          deleteLoading={deleteLoading}
        />
        {/* Loading skeleton for next page */}
        {isFetchingNextPage && (
          <div className="gallery-grid" style={{ marginTop: "12px" }}>
            {Array.from({ length: 6 }, (_, index) => (
              <div key={`skeleton-${index}`} className="gallery-item-container">
                <div className="gallery-item-skeleton">
                  <Skeleton.Image active />
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Invisible div for intersection observer */}
        <div ref={observerTarget} style={{ height: "20px" }} />
      </>
    );
  };

  return (
    <div className="col">
      <div style={{ padding: "12px" }}>
        <ScreenHeader title="Gallery" />

        <div className="gallery-description">
          <DescriptionText>
            View and browse gallery images from all child groups. Scroll down to
            load more.
          </DescriptionText>
        </div>

        <div className="gallery-filters-header">
          <ClubGroupFilter
            childGroupDetails={childGroupDetails}
            childrenGroupIds={childrenGroupIds}
            onChange={setSelectedGroupIds}
          />

          <div>
            <RangePicker
              picker="month"
              value={dateRange}
              onChange={(value) => {
                setDateRange(value || [null, null]);
              }}
              disabledDate={(current) => {
                return current && current > dayjs().endOf("month");
              }}
            />
          </div>
        </div>

        <div>{renderImages()}</div>
      </div>
    </div>
  );
};

export default ParentAdminGallery;
